import { ColumnConfig } from '@ant-design/plots';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import useSingleCompanySetting from '../../hooks/adminPage/useSingleCompanySetting';
import useReports, { ReportData } from '../../hooks/dashboardPage/useReports';
import ReportCard from './ReportCard';

const Column = dynamic(() => import('@ant-design/plots').then(({ Column }) => Column), { ssr: false });

interface BarChartProps {
    /**
     * @description set a series of colors in CSS format
     * @example ['#d62728', '#2ca02c', '#000000']
     */
    colorPattern?: string[];
    /**
     * @refreshInterval number of seconds to refresh data in given prop (url)
     * @description it will only refresh data when the time passes, it will never refresh when 0 (only when re-rendered)
     */
    refreshInterval?: number;
    title: string;
    /**
     * @url the data will be loaded from this url
     */
    url: string;
    data?: ReportData[];
}

export default function BarChart({ colorPattern, refreshInterval = 0, title, url, data = null }: BarChartProps) {
    const {
        query: { reportType }
    } = useRouter();
    const { data: zoneData } = useSingleCompanySetting('TIMEZONE');
    const zone = zoneData?.value;

    const reportUrl = data ? null : reportType ? url : zone ? `${url}?zone=${zone}` : null;

    const { data: reportData, isLoading, isValidating, mutate } = useReports<'chart'>(reportUrl, refreshInterval);

    const barChartData = useMemo(() => (data ? { d: data, c: null } : reportData), [data, reportData]);

    const updatedAt = useMemo(() => (barChartData && !isValidating ? Date.now() : null), [barChartData, isValidating]);

    const internalColorPattern = barChartData?.c ?? colorPattern;
    const isPercentageTypeReport = barChartData?.d?.every((reportData) => reportData.t === 'p');

    const config: ColumnConfig = {
        ...(internalColorPattern ? { color: internalColorPattern } : {}),
        data: barChartData?.d || [],
        label: {
            position: 'middle',
            formatter: (d) => {
                if (isPercentageTypeReport) return d.v + '%';
                return d.v;
            }
        },
        loading: isLoading,
        xAxis: {
            label: {
                autoHide: true,
                autoRotate: false
            }
        },
        // ...(isPercentageTypeReport
        //     ? {
        //           yAxis: {
        //               max: 100
        //           }
        //       }
        //     : {}),
        xField: 'k',
        yField: 'v',
        tooltip: {
            showTitle: false,
            formatter: (d) => {
                return { name: d.k, value: isPercentageTypeReport ? d.v + '%' : d.v };
            }
        }
    };

    return (
        <ReportCard
            fixedHeight
            title={title}
            updateTime={updatedAt}
            loading={isLoading}
            onButtonClick={!data ? mutate : null}
        >
            <Column {...config} />
        </ReportCard>
    );
}
